import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { Box } from '@mui/material';
import { ProjectNumberField } from 'components/Form';
import Typography from '@mui/material/Typography';
import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';
import PCPipe from '../MineModel/Pipes/PCPipe';

export interface RadarValues {
  position1?: number | undefined;
  position2?: number | undefined;
  position3?: number | undefined;
  position4?: number | undefined;
  position5?: number | undefined;
  position6?: number | undefined;
  position7?: number | undefined;
  position8?: number | undefined;
}

interface PipeCrossSectionProps {
  wearDataNew: RadarValues;
  pipeData: PCPipe;
}

const PipeCrossSection = ({ wearDataNew, pipeData }: PipeCrossSectionProps) => {
  const hasValidPipeData = pipeData.insideDiameter
    && pipeData.minimumThickness
    && pipeData.startingThickness;

  const svgRef = useRef(null);

  const [exagerationValue, setExagerationValue] = useState(3);

  const wearValues = [
    wearDataNew.position1 ?? 0,
    wearDataNew.position2 ?? 0,
    wearDataNew.position3 ?? 0,
    wearDataNew.position4 ?? 0,
    wearDataNew.position5 ?? 0,
    wearDataNew.position6 ?? 0,
    wearDataNew.position7 ?? 0,
    wearDataNew.position8 ?? 0,
  ].map((value) => (Number.isNaN(value) ? 0 : value));

  const wearDataSets = [wearValues];

  const originalInnerRadius = (pipeData.insideDiameter * 1000) / 2;
  const startingThicknessmm = (pipeData.startingThickness || 0) * 1000;
  const outerRadius = originalInnerRadius + startingThicknessmm;
  const minimumThicknessmm = (pipeData.minimumThickness || 0) * 1000;

  const exageratedThickness = startingThicknessmm * exagerationValue;
  const exageratedInnerRadius = outerRadius - exageratedThickness;

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous drawing

    const viewBoxWidth = 400;
    const viewBoxHeight = 400;
    const centerX = viewBoxWidth / 2;
    const centerY = viewBoxHeight / 2;

    // Determine the maximum extent of the drawing including labels
    const lineOffset = 10;
    const labelOffset = 20;
    const maxRadiusToFit = 100;

    // Calculate scale factor to fit everything in the viewBox
    const padding = 20;
    const effectiveViewBoxWidth = viewBoxWidth - (padding * 2);
    const effectiveViewBoxHeight = viewBoxHeight - (padding * 2);

    let scaleFactor = 1;
    if (maxRadiusToFit * 2 > 0) {
        scaleFactor = Math.min(
            effectiveViewBoxWidth / (maxRadiusToFit * 2),
            effectiveViewBoxHeight / (maxRadiusToFit * 2),
        );
    }

    if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
        scaleFactor = 1;
    }

    const angleStep = (2 * Math.PI) / wearDataSets[0].length;

    // Color scale: lower thickness = more wear = red, higher thickness = less wear = green
    const colorScale = d3
      .scaleLinear<string>()
      .domain([minimumThicknessmm, startingThicknessmm])
      .range(['red', 'green']); // Red for thin (worn), green for thick (good)

    // Draw reference outer circle (original pipe outer diameter)
    svg
      .append('circle')
      .attr('cx', centerX)
      .attr('cy', centerY)
      .attr('r', outerRadius * scaleFactor)
      .style('fill', 'lightgray');

    // Function to generate points for current thickness measurements
    const getPoints = (wearData: number[]): [number, number][] => wearData.map((thickness, index) => {
      const angle = index * angleStep - Math.PI / 2;
      const exageratedWearThickness = thickness * exagerationValue;
      let wearRadius = 0;

      if (thickness) {
        wearRadius = Math.max(outerRadius - exageratedWearThickness, exageratedInnerRadius) * scaleFactor;
      } else {
        wearRadius = exageratedInnerRadius * scaleFactor;
      }

      return [
        centerX + Math.cos(angle) * wearRadius,
        centerY + Math.sin(angle) * wearRadius,
      ];
    });

    if (hasValidPipeData) {
      // Draw each wear data set
      wearDataSets.forEach((wearData, dataSetIndex) => {
        const points = getPoints(wearData);
        const avgThickness = (d3.min(wearData) ?? startingThicknessmm); // Average thickness for color determination
        const color = colorScale(avgThickness);

        const fillPath = svg
          .append('path')
          .datum(points)
          .attr('d', d3.line().curve(d3.curveCardinalClosed));

        if (dataSetIndex === 0) {
          // First wear data set: white fill and solid stroke
          fillPath
            .attr('fill', 'white')
            .attr('stroke', color)
            .attr('stroke-width', 1);
        } else {
          // Subsequent wear data sets: no fill and dashed stroke
          fillPath
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '4,4');
        }
      });

      // Draw the inner diameter (dotted line)
      svg
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', exageratedInnerRadius * scaleFactor)
        .attr('fill', 'none')
        .attr('stroke', 'gray')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '2,2');
    }

    for (let radius = 10; radius < outerRadius * scaleFactor; radius += (10 * scaleFactor)) {
      svg
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', radius)
        .attr('fill', 'none')
        .attr('stroke', 'lightgray')
        .attr('stroke-width', 0.5);
    }

    // Draw radial lines and labels (1-8)
    for (let i = 0; i < wearDataSets[0].length; i += 1) {
      const angle = i * angleStep - Math.PI / 2;
      const xOuter = centerX + Math.cos(angle) * ((outerRadius + lineOffset) * scaleFactor); // Extend beyond outer circle
      const yOuter = centerY + Math.sin(angle) * ((outerRadius + lineOffset) * scaleFactor);

      svg
        .append('line')
        .attr('x1', centerX)
        .attr('y1', centerY)
        .attr('x2', xOuter)
        .attr('y2', yOuter)
        .attr('stroke', 'black')
        .attr('stroke-width', 1);

      // Add labels (1-8) at the end of each radial line
      const labelX = centerX + Math.cos(angle) * ((outerRadius + labelOffset) * scaleFactor); // Position label beyond outer radius
      const labelY = centerY + Math.sin(angle) * ((outerRadius + labelOffset) * scaleFactor);

      svg
        .append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('font-size', '16px')
        .attr('fill', 'white')
        .text(i + 1); // Label with numbers 1-8
    }

    // Draw minimum thickness line on top (if available)
    if (hasValidPipeData && pipeData.minimumThickness) {
      const exageratedMinimumThickness = minimumThicknessmm * exagerationValue;
      const minimumThicknessRadius = Math.max(outerRadius - exageratedMinimumThickness, exageratedInnerRadius) * scaleFactor;

      svg
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', minimumThicknessRadius)
        .attr('fill', 'none')
        .attr('stroke', 'red')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,3');
    }
  }, [wearDataSets, originalInnerRadius, outerRadius, minimumThicknessmm, startingThicknessmm, pipeData.startingThickness, hasValidPipeData, exagerationValue]);

  return (
    <>
      <svg
        ref={svgRef}
        width="400"
        height="400"
        viewBox="0 0 400 400"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
      />
      {!hasValidPipeData && (
        <Typography variant="subtitle2">
          <Trans id="Unable to plot pipe dimensions as data unavailable">
            Unable to plot pipe dimensions as data unavailable
          </Trans>
        </Typography>
      )}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <ProjectNumberField
          label={i18n._('Thickness Exaggeration')}
          decimalPlaces={0}
          value={exagerationValue}
          onChange={(parsed) => setExagerationValue(Math.min(Math.max(parsed ?? 1, 1), 10))}
          size="small"
          sx={{ width: 175 }}
        />
      </Box>
    </>
  );
};

export default PipeCrossSection;
